import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Input, Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/helper";
import { REPORT_MIN_LENGTH } from "../scheduleStatus";
import { MEETING_APP } from "../meetingAdminApp";
import AdminHebatReportFields from "./AdminHebatReportFields";

const { TextArea } = Input;

// Tinggi textarea disamakan dengan panel AdminHebat di sebelahnya, sekaligus
// memberi ruang ketik yang lega untuk catatan meeting yang panjang.
const REPORT_ROWS = 16;

const MeetingReportModal = ({
  open,
  defaultReport = "",
  loading = false,
  onSubmit,
  onCancel,
  isInternal = false,
  contactId = null,
  contactLoading = false,
  defaultAppId = MEETING_APP.FINANCE,
}) => {
  const { t } = useTranslation();
  const [report, setReport] = useState(defaultReport);
  const [adminHebat, setAdminHebat] = useState({
    appId: defaultAppId,
    company: null,
  });
  const textAreaRef = useRef(null);

  // Prefill ulang setiap kali modal dibuka.
  useEffect(() => {
    if (open) {
      setReport(defaultReport);
      setAdminHebat({
        appId: defaultAppId,
        company: null,
      });
    }
  }, [open, defaultReport, defaultAppId]);

  // Fokuskan kursor ke textarea setelah animasi buka modal selesai
  // (autoFocus kalah dengan focus-trap milik Modal).
  const handleAfterOpenChange = (isOpen) => {
    if (isOpen) {
      textAreaRef.current?.focus({ cursor: "end" });
    }
  };

  const reportTooShort = report.trim().length < REPORT_MIN_LENGTH;
  // AdminHebat wajib untuk website internal → perusahaan harus dipilih.
  const adminHebatMissingCompany = isInternal && !adminHebat.company;
  const canSubmit = !reportTooShort && !adminHebatMissingCompany && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Argumen kedua = app terpilih untuk brand header pratinjau WhatsApp
    // (selalu ada di state; terpisah dari payload backend).
    onSubmit?.(
      {
        report: report.trim(),
        ...(isInternal && adminHebat.company
          ? { company_id: adminHebat.company.id, app_id: adminHebat.appId }
          : {}),
      },
      adminHebat.appId
    );
  };

  return (
    <Modal
      open={open}
      title={t("meeting_management.meeting_report")}
      onCancel={onCancel}
      afterOpenChange={handleAfterOpenChange}
      maskClosable={false}
      destroyOnHidden
      // Dua kolom hanya perlu ruang ekstra saat panel AdminHebat ikut tampil.
      width={isInternal ? 900 : 560}
      footer={
        <div className="flex items-center justify-between">
          <Button
            icon={<CloseOutlined />}
            onClick={onCancel}
            disabled={loading}
          >
            {t("button.cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {t("meeting_finished.save")}
          </Button>
        </div>
      }
    >
      {/* Kiri: hasil meeting. Kanan: laporan AdminHebat. Menumpuk di layar
          sempit supaya textarea tidak jadi kekecilan. */}
      <div
        className={cn(
          "gap-4",
          isInternal ? "grid md:grid-cols-2" : "space-y-4"
        )}
      >
        <div className="space-y-2">
          <p className="mb-0 text-sm font-semibold text-neutral-800">
            {t("meeting_finished.report_question")}
          </p>
          <TextArea
            ref={textAreaRef}
            autoFocus
            value={report}
            onChange={(e) => setReport(e.target.value)}
            rows={REPORT_ROWS}
            showCount
            maxLength={1000}
            placeholder={t("meeting_finished.report_placeholder")}
          />
          <p
            className={
              reportTooShort
                ? "mb-0 text-xs text-red-500"
                : "mb-0 text-xs text-neutral-400"
            }
          >
            {t("meeting_finished.report_min", { min: REPORT_MIN_LENGTH })}
          </p>
        </div>

        {isInternal && (
          <AdminHebatReportFields
            contactId={contactId}
            contactLoading={contactLoading}
            value={adminHebat}
            onChange={setAdminHebat}
          />
        )}
      </div>
    </Modal>
  );
};

export default MeetingReportModal;
